/**
 * Minimale, dependency-vrije XLSX-schrijver.
 *
 * Waarom geen kant-en-klare bibliotheek: we hebben maar een fractie van de
 * functionaliteit nodig (een tabblad, kopregel, tekst- en getalcellen), en
 * elke afhankelijkheid die in de telefoonbundel belandt kost laadtijd.
 *
 * Een .xlsx is een ZIP met OOXML-onderdelen erin. Het zippen zelf gebeurt
 * in zip.ts; hier staat alleen wat er in die onderdelen moet komen.
 */

import { maakZip } from './zip.ts';

export type CelWaarde = string | number | boolean | null | undefined;

export interface Kolom {
  kop: string;
  breedte?: number;
}

/* ------------------------------------------------------------------ */
/* OOXML                                                               */
/* ------------------------------------------------------------------ */

function escapeXml(waarde: string): string {
  return waarde
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    // Excel weigert stuurtekens in XML.
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');
}

function kolomLetter(index: number): string {
  let n = index + 1;
  let letters = '';
  while (n > 0) {
    const rest = (n - 1) % 26;
    letters = String.fromCharCode(65 + rest) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
}

function cel(kolomIndex: number, rijNummer: number, waarde: CelWaarde, stijl: number): string {
  const ref = `${kolomLetter(kolomIndex)}${rijNummer}`;
  const s = stijl ? ` s="${stijl}"` : '';

  if (waarde === null || waarde === undefined || waarde === '') {
    return `<c r="${ref}"${s}/>`;
  }
  if (typeof waarde === 'number' && Number.isFinite(waarde)) {
    return `<c r="${ref}"${s}><v>${waarde}</v></c>`;
  }
  const tekst = typeof waarde === 'boolean' ? (waarde ? 'Ja' : 'Nee') : String(waarde);
  return `<c r="${ref}"${s} t="inlineStr"><is><t xml:space="preserve">${escapeXml(tekst)}</t></is></c>`;
}

const CONTENT_TYPES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`;

const ROOT_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

const WORKBOOK_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

/** Stijl 0 = normaal, stijl 1 = vet met lichte vulling (kopregel). */
const STYLES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font></fonts>
<fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FF0F6CBD"/><bgColor indexed="64"/></patternFill></fill></fills>
<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/></cellXfs>
</styleSheet>`;

function workbookXml(tabbladNaam: string): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets><sheet name="${escapeXml(tabbladNaam.slice(0, 31))}" sheetId="1" r:id="rId1"/></sheets>
</workbook>`;
}

function sheetXml(kolommen: Kolom[], rijen: CelWaarde[][]): string {
  const cols = kolommen
    .map(
      (kolom, i) =>
        `<col min="${i + 1}" max="${i + 1}" width="${kolom.breedte ?? 18}" customWidth="1"/>`
    )
    .join('');

  const kopRij = `<row r="1">${kolommen
    .map((kolom, i) => cel(i, 1, kolom.kop, 1))
    .join('')}</row>`;

  const dataRijen = rijen
    .map((rij, rijIndex) => {
      const nummer = rijIndex + 2;
      const cellen = kolommen
        .map((_, kolomIndex) => cel(kolomIndex, nummer, rij[kolomIndex], 0))
        .join('');
      return `<row r="${nummer}">${cellen}</row>`;
    })
    .join('');

  const laatsteKolom = kolomLetter(Math.max(0, kolommen.length - 1));

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
<cols>${cols}</cols>
<sheetData>${kopRij}${dataRijen}</sheetData>
<autoFilter ref="A1:${laatsteKolom}${rijen.length + 1}"/>
</worksheet>`;
}

/** Bouwt een volledig .xlsx-bestand als Blob. */
export function maakXlsx(
  kolommen: Kolom[],
  rijen: CelWaarde[][],
  tabbladNaam = 'Blad1'
): Blob {
  return maakZip([
    { naam: '[Content_Types].xml', inhoud: CONTENT_TYPES },
    { naam: '_rels/.rels', inhoud: ROOT_RELS },
    { naam: 'xl/workbook.xml', inhoud: workbookXml(tabbladNaam) },
    { naam: 'xl/_rels/workbook.xml.rels', inhoud: WORKBOOK_RELS },
    { naam: 'xl/styles.xml', inhoud: STYLES },
    { naam: 'xl/worksheets/sheet1.xml', inhoud: sheetXml(kolommen, rijen) },
  ], 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
}
